import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, TextField, Select, MenuItem, InputLabel, FormControl } from '@material-ui/core';
import Http from '../../../Http';
import LeafletMap from './LeafletMap';

const Colonia = () => {
  const history = useHistory();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    colonia: '',
    codigo_postal: '',
    id_estado: '',
    id_ciudad: '',
    lat: 23.634501, // default Mexico
    lon: -102.552784,
    alt: 0,
    utc: '',
  });

  const [options, setOptions] = useState({
    estados: [],
    ciudades: []
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, [id]);

  const loadInitialData = async () => {
    setLoading(true);
    let currentData = { ...formData };
    
    // Si estamos editando, cargar data de DB
    if (id) {
      const res = await Http.get(`colonia/one/${id}`);
      if (!res.error && res.data) {
        currentData = { ...currentData, ...res.data };
        setFormData(currentData);
      }
    } else {
      // Si es nuevo, inicializar los catálogos vacíos primero
      await fetchGenerales();
    }
    
    // Cargar options basados en CP o Estado
    if (currentData.codigo_postal || currentData.id_estado) {
        await fetchGenerales(currentData.codigo_postal, currentData.id_estado);
    }
    setLoading(false);
  };

  const fetchGenerales = async (cp, estadoId) => {
    let url = `cliente/form-generales?`;
    if (estadoId) url += `e=${estadoId}&`;
    if (cp && cp.toString().length === 5) url += `cp=${cp}&`;

    const res = await Http.get(url);
    if (!res.error && res.data) {
      const newOptions = {
        estados: res.data.estados || options.estados,
        ciudades: res.data.ciudades || options.ciudades
      };
      
      // Si el backend manda direccion por CP
      if (res.data.direccion) {
        newOptions.ciudades = res.data.direccion.ciudades;
        setFormData(prev => ({
          ...prev,
          id_estado: res.data.direccion.estado.id,
          id_ciudad: res.data.direccion.ciudad.id
        }));
      }
      setOptions(prevOptions => ({
         estados: newOptions.estados.length ? newOptions.estados : prevOptions.estados,
         ciudades: newOptions.ciudades.length ? newOptions.ciudades : prevOptions.ciudades,
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'codigo_postal' && value.length === 5) {
      fetchGenerales(value, formData.id_estado);
    }
    if (name === 'id_estado') {
      fetchGenerales(formData.codigo_postal, value);
    }
  };

  const handleMapChange = (lat, lon) => {
    setFormData(prev => ({ ...prev, lat, lon }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await Http.post('colonia/save', formData);
    setLoading(false);
    if (!res.error) {
      history.push('/Catalogo/Colonias');
    } else {
      alert(res.message || 'Error al guardar');
    }
  };

  return (
    <div className="w-full p-4 md:p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
          {id ? 'Editar Colonia' : 'Nueva Colonia'}
        </h1>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => history.push('/Catalogo/Colonias')}
        >
          Regresar
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        {loading && !id ? (
          <div className="text-center py-4">Cargando...</div>
        ) : (
          <form onSubmit={handleSave} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Izquierda: Inputs del Formulario */}
            <div className="space-y-6">
              <TextField
                fullWidth
                label="Nombre de la colonia"
                name="colonia"
                value={formData.colonia || ''}
                onChange={handleChange}
                required
                variant="outlined"
              />

              <TextField
                fullWidth
                label="Código Postal"
                name="codigo_postal"
                value={formData.codigo_postal || ''}
                onChange={handleChange}
                required
                variant="outlined"
                inputProps={{ maxLength: 5 }}
              />

              <FormControl fullWidth variant="outlined" required>
                <InputLabel id="estado-label">Estado</InputLabel>
                <Select
                  labelId="estado-label"
                  name="id_estado"
                  value={formData.id_estado || ''}
                  onChange={handleChange}
                  label="Estado"
                >
                  <MenuItem value=""><em>Seleccione un Estado</em></MenuItem>
                  {options.estados.map(est => (
                    <MenuItem key={est.id} value={est.id}>{est.estado}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth variant="outlined" required>
                <InputLabel id="ciudad-label">Ciudad</InputLabel>
                <Select
                  labelId="ciudad-label"
                  name="id_ciudad"
                  value={formData.id_ciudad || ''}
                  onChange={handleChange}
                  label="Ciudad"
                >
                  <MenuItem value=""><em>Seleccione una Ciudad</em></MenuItem>
                  {options.ciudades.map(ciu => (
                    <MenuItem key={ciu.id} value={ciu.id}>{ciu.ciudad}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <div className="flex gap-4">
                <TextField
                  fullWidth
                  label="Latitud"
                  name="lat"
                  type="number"
                  inputProps={{ step: "any" }}
                  value={formData.lat || ''}
                  onChange={handleChange}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Longitud"
                  name="lon"
                  type="number"
                  inputProps={{ step: "any" }}
                  value={formData.lon || ''}
                  onChange={handleChange}
                  variant="outlined"
                />
              </div>

              <div className="pt-4 flex justify-end">
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  Guardar Colonia
                </Button>
              </div>
            </div>

            {/* Derecha: Mapa Leaflet (Deshabilitado temporalmente) */}
            {/* <div className="h-full min-h-[400px] border border-gray-200 rounded-lg overflow-hidden relative" style={{zIndex: 0}}>
               <LeafletMap 
                  lat={formData.lat} 
                  lon={formData.lon} 
                  onChange={handleMapChange} 
               />
               <div className="absolute top-2 left-2 z-[1000] bg-white bg-opacity-90 p-2 rounded shadow text-sm font-semibold pointer-events-none">
                 Arrastra el marcador o da clic en el mapa
               </div>
            </div> */}

          </form>
        )}
      </div>
    </div>
  );
};

export default Colonia;